import { useEffect, useMemo, useState } from 'react';
import { useManufacturersPage } from '../../services/api/domains/manufacturers/hooks';
import * as XLSX from 'xlsx';
import { Manufacturer } from 'services/api/domains/manufacturers/types';

interface UploadProductsProps {
  isLoading: boolean;
  confirmManufacturer: (
    manufacturerId: number | null,
    file: File | null
  ) => void;
  closeModal: () => void;
}

const UploadProductsModal: React.FC<UploadProductsProps> = ({
  isLoading,
  closeModal,
  confirmManufacturer,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 50; // page size
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const params = useMemo(
    () => ({ limit, offset, search: searchTerm }),
    [limit, offset, searchTerm]
  );
  const { data: manufacturersManagerData } = useManufacturersPage(params);
  const [chosenManufacturer, setChosenManufacturer] = useState<number | null>(
    null
  );
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      const file = e.target.files[0];
      try {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.SheetNames[0];
        if (firstSheet.toUpperCase() !== 'LGMAPILOADER') {
          alert(
            `❌ Spreadsheet not preprocessed. First tab must be 'LGMAPILOADER'. Found: ${firstSheet}`
          );
          return;
        }
        setUploadFile(file);
      } catch {
        alert('❌ Failed to read Excel file.');
      }
    }
  };

  // Reset manufacturers when search changes
  useEffect(() => {
    setManufacturers([]);
    setOffset(0);
    setHasMore(true);
  }, [searchTerm]);

  // Append new manufacturers when data loads
  useEffect(() => {
    if (manufacturersManagerData && manufacturersManagerData.length > 0) {
      setManufacturers(prev => {
        // Avoid duplicates
        const ids = new Set(prev.map(m => m.manufacturer_id));
        const newItems = manufacturersManagerData.filter(
          m => !ids.has(m.manufacturer_id)
        );
        return [...prev, ...newItems];
      });
      if (manufacturersManagerData.length < limit) {
        setHasMore(false);
      }
    }
  }, [manufacturersManagerData, limit]);

  return (
    <div style={styles.modalOverlayStyle}>
      <div style={styles.uploadModalContentStyle}>
        <h2 style={styles.modalTitleStyle}>Upload Product Data</h2>
        <div style={styles.searchSectionStyle}>
          <input
            type="text"
            placeholder="Search manufacturers..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={styles.searchInputStyle}
          />
          <div
            style={styles.manufacturerListContainerStyle}
            onScroll={e => {
              const el = e.currentTarget;
              if (
                hasMore &&
                el.scrollHeight - el.scrollTop - el.clientHeight < 40
              ) {
                // Near bottom, fetch next page
                setOffset(prev => prev + limit);
              }
            }}>
            {manufacturers.length === 0 && (
              <div style={styles.noResultsStyle}>No manufacturers found.</div>
            )}
            {manufacturers.map(m => (
              <div
                key={m.manufacturer_id}
                style={{
                  ...styles.manufacturerRowStyle,
                  background:
                    chosenManufacturer === m.manufacturer_id
                      ? '#e0e7ff'
                      : '#fff',
                  cursor: 'pointer',
                }}
                onClick={() => setChosenManufacturer(m.manufacturer_id)}>
                {m.manufacturer_name}
              </div>
            ))}
            {hasMore && (
              <div style={styles.loadingMoreStyle}>Loading more…</div>
            )}
          </div>
        </div>
        <input
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileChange}
          style={styles.uploadFileInputStyle}
        />
        <div style={styles.uploadModalActionsStyle}>
          <button
            onClick={() => closeModal()}
            style={styles.uploadCancelButtonStyle}>
            Cancel
          </button>
          <button
            onClick={() => confirmManufacturer(chosenManufacturer, uploadFile)}
            style={styles.uploadConfirmButtonStyle}
            disabled={isLoading}>
            {isLoading ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  manufacturerListContainerStyle: {
    maxHeight: '240px',
    overflowY: 'auto',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    background: '#f9fafb',
    marginBottom: '16px',
  },
  manufacturerRowStyle: {
    padding: '10px 12px',
    borderBottom: '1px solid #e5e7eb',
    fontSize: '15px',
    transition: 'background 0.2s',
  },
  noResultsStyle: {
    padding: '16px',
    color: '#888',
    textAlign: 'center',
  },
  loadingMoreStyle: {
    padding: '12px',
    color: '#666',
    textAlign: 'center',
    fontSize: '14px',
  },
  searchSectionStyle: {
    marginBottom: '16px',
  },
  searchInputStyle: {
    width: '100%',
    marginBottom: '8px',
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #d1d5db',
  },
  paginationControlsStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '8px',
  },
  paginationButtonStyle: {
    padding: '4px 12px',
    borderRadius: '4px',
    border: '1px solid #d1d5db',
  },
  paginationInfoStyle: {
    fontSize: '12px',
    color: '#666',
  },
  modalOverlayStyle: {
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    backgroundColor: 'rgba(0, 0, 0, 0.5)', // bg-black bg-opacity-50
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
  },
  uploadModalContentStyle: {
    backgroundColor: '#ffffff', // bg-white
    padding: '24px', // p-6
    borderRadius: '8px', // rounded-lg
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', // shadow-lg
    width: '384px', // w-96
  },
  diffModalTitleStyle: {
    fontSize: '20px', // text-xl
    fontWeight: 'bold', // font-bold
  },
  uploadSelectStyle: {
    border: '1px solid #d1d5db', // border
    borderRadius: '4px', // rounded
    padding: '8px 12px', // px-3 py-2
    marginBottom: '16px', // mb-4
    width: '100%', // w-full
  },
  uploadFileInputStyle: {
    marginBottom: '16px', // mb-4
  },
  uploadModalActionsStyle: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px', // space-x-2
  },
  uploadCancelButtonStyle: {
    backgroundColor: '#d1d5db', // bg-gray-300
    padding: '8px 16px', // px-4 py-2
    borderRadius: '4px', // rounded
    border: 'none',
    cursor: 'pointer',
  },
  uploadConfirmButtonStyle: {
    backgroundColor: '#2563eb', // bg-blue-600
    color: '#ffffff', // text-white
    padding: '8px 16px', // px-4 py-2
    borderRadius: '4px', // rounded
    border: 'none',
    cursor: 'pointer',
  },
};

export default UploadProductsModal;
